// Zähler für die Sidebar-Badges (Handlungsbedarf). Reine Frontend-Aggregation
// über bestehende Services/Contexts — kein neuer Backend-Endpoint:
//  - vorgaenge: OFFENE Vorgänge, dem User PERSÖNLICH zugeordnet (wie Dashboard).
//  - mails:     UNGELESENE Mails im Posteingang der Postfächer, die die Person
//               zählen lässt (T-249, Mail-Einstellungen → Postfächer);
//               Standard: nur das eigene Postfach.
//  - calls:     PERSÖNLICH verpasste (nur eingehende, `isMissedInbound`) & noch
//               OFFENE (resolvedAt = null) Anrufe der letzten 30 Tage — für ALLE
//               Rollen inkl. admin (User-Entscheidungen #52/#54, Review 2026-07-05).
//               Zentrale-Anrufe (ohne Nebenstelle/User) zählt der Server nur beim
//               admin in den Persönlich-Scope (Entscheidung #53).
// Alles fail-safe (Fehler → 0) und im 60-s-Takt aktualisiert.
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useMailContext } from "../context/MailContext";
import { vorgangService, type VorgangDto } from "../services/vorgangService";
import { vorgangMatchesScope, type ScopeCtx } from "../utils/vorgangScope";
import { ANRUFE_GEAENDERT, telephonyService, type CallLogDto } from "../services/telephonyService";
import { isOpenMissed } from "../components/telephony/telephonyUtils";
import { deriveVorgangStatus } from "../components/vorgang/shared/vorgangStatus";
import { glockenZahl } from "../components/mail/glockenPostfaecher";
import { useGlockenPostfaecher } from "../components/mail/useMailPostfachEinstellungen";

const POLL_MS = 60_000;
// Badge-Zeitfenster: nur Anrufe der letzten 30 Tage zählen (Entscheidung #54 —
// statt einmaliger Alt-Erledigung; der Alt-Bestand bleibt in der Liste sichtbar).
const BADGE_WINDOW_DAYS = 30;

/** „Offen" wie überall (`deriveVorgangStatus`) — ein von Hand erledigter
 *  Vorgang ohne To-dos zählt nicht mit (T-156; konsistent mit dem „V" der
 *  Kontaktliste und dem OffeneVorgaengeDialog). */
export function isVorgangOpen(v: VorgangDto): boolean {
  return deriveVorgangStatus(v) === "Offen";
}

/** Default-Scope für das Anruf-Badge: „vorerst für ALLE die persönlich verpassten"
 *  (A1-Präzisierung + Entscheidung #52 — auch der admin zählt persönlich; der
 *  Scope bleibt auf der Telefon-Seite umschaltbar). Server-seitig bedeutet `own`:
 *  eigene Anrufe ODER Anrufe eigener Kontakte; beim admin zusätzlich
 *  Zentrale-Anrufe ohne Nebenstelle/User (Entscheidung #53). */
export function defaultCallScope(): "all" | "own" | "team" {
  return "own";
}

export interface NavBadges {
  vorgaenge: number;
  mails: number;
  calls: number;
  /**
   * Die offenen verpassten Anrufe hinter `calls` (dieselbe Abfrage, kein
   * zweiter Poller) — das Mitteilungszentrum zeigt sie einzeln.
   */
  verpassteAnrufe?: CallLogDto[];
}

/** Liefert die drei Badge-Zähler. `enabled=false` (z. B. nicht eingeloggt)
 *  hält alles bei 0 und startet keine Abfragen/Timer. */
export function useNavBadges(enabled: boolean): NavBadges {
  const { user, teammates, teams } = useAuth();
  const callScope = defaultCallScope();
  const { unreadByMailbox, ownMailbox } = useMailContext();
  const { wert: glockenWahl } = useGlockenPostfaecher(enabled);
  const [vorgaenge, setVorgaenge] = useState(0);
  const [calls, setCalls] = useState(0);
  const [verpassteAnrufe, setVerpassteAnrufe] = useState<CallLogDto[]>([]);
  // Mails-Badge: Summe der Posteingang-Ungelesenen je Postfach (nur der echte
  // Inbox-Ordner, ohne Unterordner/Junk/Gelöscht/Archiv) aus dem MailContext —
  // live (PR5); kein eigener Poll/Fetch mehr.
  //
  // T-249 (08.10.2026): Bis hierher zählten ALLE lesbaren Postfächer — cbb@ und
  // schaden@ inklusive. Glocke und Posteingang zeigten deshalb verschiedene
  // Zahlen („34 im Postfach, 43 bei den Benachrichtigungen"). Jetzt zählen nur
  // die eingestellten Postfächer, im Standard das eigene.
  const mails = enabled ? glockenZahl(unreadByMailbox, glockenWahl, ownMailbox) : 0;

  const scopeCtx: ScopeCtx = useMemo(
    () => ({
      userId: user?.id ?? null,
      teammates: teammates ?? [],
      teamIds: (teams ?? []).map((t) => t.id),
    }),
    [user?.id, teammates, teams],
  );

  // Offene Vorgänge (persönlich)
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const run = () => {
      if (document.hidden) return; // versteckter Tab: nicht pollen
      vorgangService
        .getAllVorgaenge({})
        .then((list) => {
          if (!alive) return;
          setVorgaenge(
            list.filter((v) => vorgangMatchesScope(v, "persoenlich", scopeCtx) && isVorgangOpen(v)).length,
          );
        })
        .catch(() => alive && setVorgaenge(0));
    };
    run();
    const t = window.setInterval(run, POLL_MS);
    const onVis = () => { if (!document.hidden) run(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      alive = false;
      window.clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [enabled, scopeCtx]);


  // Persönlich verpasste (inbound) & noch offene Anrufe der letzten 30 Tage.
  // Missed-Definition vereinheitlicht auf `isOpenMissed` (wie Telefon-Seite):
  // erfolglose AUSGEHENDE Anrufe zählen nicht, erledigte verpasste ebenfalls nicht.
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const run = () => {
      if (document.hidden) return; // versteckter Tab: nicht pollen
      const from = new Date(Date.now() - BADGE_WINDOW_DAYS * 86_400_000).toISOString();
      // `missedOnly`: Der Server filtert mit derselben Bedingung (OFFEN_VERPASST_SQL
      // ↔ isOpenMissed). Vorher kamen JEDE Minute alle Anrufe der 30 Tage (DEV:
      // ~475 KB je Abruf), obwohl nur die offenen verpassten gebraucht werden.
      telephonyService
        .listCalls({ limit: 500, scope: callScope, from, missedOnly: true })
        .then((res) => {
          if (!alive) return;
          const offen = res.calls.filter(isOpenMissed);
          setCalls(offen.length);
          setVerpassteAnrufe(offen);
        })
        .catch(() => {
          if (!alive) return;
          setCalls(0);
          setVerpassteAnrufe([]);
        });
    };
    run();
    const t = window.setInterval(run, POLL_MS);
    const onVis = () => { if (!document.hidden) run(); };
    document.addEventListener("visibilitychange", onVis);
    // Anderswo erledigt (z. B. Arbeitsansicht der Kontaktliste) → gleich neu zählen.
    window.addEventListener(ANRUFE_GEAENDERT, run);
    return () => {
      alive = false;
      window.clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener(ANRUFE_GEAENDERT, run);
    };
  }, [enabled, callScope]);

  return { vorgaenge, mails, calls, verpassteAnrufe: enabled ? verpassteAnrufe : [] };
}
